// What a brand new machine already knows.
//
// A barbell app that opens on "no kit, no plates, no lifts" has asked the lifter
// to do data entry before it has proved it can do anything. So it opens knowing
// what a standard competition platform looks like, and Kit exists to CORRECT
// that rather than to create it from nothing.
//
// NOTHING HERE MAY RUN BEFORE `storeReady`. Ids come from one shared sequence,
// and a row allocated before the store has hydrated collides with a stored one.

import {
  kits, kitsReady, addKit,
  plates, platesReady, addPlate,
  lifts, liftsReady, addLift, saveLift, findLift,
  rampSteps, rampStepsReady, addRampStep, saveRampStep, removeRampStep,
} from '../store.js';
import { jumpRule, rangeTo, tidyScheme } from './loading.js';

// IPF competition colours, as commonly used. These are SEED DATA on the plate
// record, never tokens in a stylesheet: a lifter whose gym has black 25s
// recolours them in Kit and every drawing in the app follows.
const KG_PLATES = [
  { weight: 25, pairs: 6, colour: '#D0202E' },
  { weight: 20, pairs: 2, colour: '#1F4E9C' },
  { weight: 15, pairs: 2, colour: '#F2C12E' },
  { weight: 10, pairs: 2, colour: '#2F8A4C' },
  { weight: 5, pairs: 2, colour: '#F4F4F1' },
  { weight: 2.5, pairs: 2, colour: '#1B1D1F' },
  { weight: 1.25, pairs: 2, colour: '#B8BDC1' },
  { weight: 0.5, pairs: 2, colour: '#B8BDC1' },
  { weight: 0.25, pairs: 2, colour: '#B8BDC1' },
];

// An lb kit gets its own seed rather than a conversion: American plates are
// their own sizes, not metric ones rounded.
export const LB_PLATES = [
  { weight: 45, pairs: 6, colour: '#1F4E9C' },
  { weight: 35, pairs: 2, colour: '#F2C12E' },
  { weight: 25, pairs: 2, colour: '#2F8A4C' },
  { weight: 10, pairs: 2, colour: '#1B1D1F' },
  { weight: 5, pairs: 2, colour: '#B8BDC1' },
  { weight: 2.5, pairs: 2, colour: '#B8BDC1' },
];

// The default ramp, the same for all three lifts: the arithmetic a lifter the
// owner trusts runs on his phone. The first is half the top set; the rest hang
// off the LAST warm-up (top minus the jump), so a bigger jump pulls them down
// with it. 180 with a 10kg jump: 90 × 5, 120 × 3, 135 × 1–2, 157.5 × 1–2, then
// 170 × 1. Warm-up scheme is where a lifter makes it theirs.
export const DEFAULT_STEPS = [
  { percent: 50, of: 'top', reps: 5, repsTo: null },
  { percent: 70, of: 'last', reps: 3, repsTo: null },
  { percent: 80, of: 'last', reps: 1, repsTo: 2 },
  { percent: 92.5, of: 'last', reps: 1, repsTo: 2 },
];

// startWeight is where the FIRST warm-up sits, and it is not the same question
// for every lift. A squat and a bench start on an empty bar — the bar alone,
// because collars go on with the first plate. A deadlift starts at PLATE HEIGHT
// — bar plus a pair of 20s, collared — because pulling from a lower bar is a
// different exercise, not a lighter one.
const startFor = (kind, kit) =>
  kind === 'deadlift' ? kit.barWeight + 2 * (kit.collarWeight || 0) + 40
    : kit.barWeight;

// An empty bar is a range, done until the groove is there; a pull from plate
// height is five.
const startRepsFor = (kind) => (kind === 'deadlift' ? { startReps: 5, startRepsTo: null } : { startReps: 6, startRepsTo: 10 });

// Which default a lift has been checked against. Marked once, so a lifter who
// later types the first default back in on purpose keeps it.
// 2: the lifter's formula replaced the first default. 3: an empty bar has no
// collars, so a start on bar-and-collars moved to the bar alone.
const SCHEME_VERSION = 3;

export async function seedIfEmpty() {
  await Promise.all([kitsReady, platesReady, liftsReady, rampStepsReady]);

  // Any one of these already existing means this device has been used. Seeding
  // on top of that would hand somebody a second Squat every launch.
  if (kits.value.length || plates.value.length || lifts.value.length) return false;

  const kit = addKit({ name: 'Comp bar', unit: 'kg', barWeight: 20, collarWeight: 2.5 });
  for (const p of KG_PLATES) addPlate({ ...p, kit: kit.id });

  for (const [name, kind] of [['Squat', 'squat'], ['Bench', 'bench'], ['Deadlift', 'deadlift']]) {
    const lift = addLift({
      name,
      kind,
      topSet: null,
      topSetReps: 1,
      startWeight: startFor(kind, kit),
      ...startRepsFor(kind),
      lastJump: jumpRule(kit.unit).usual,
      schemeVersion: SCHEME_VERSION,
      kit: kit.id,
    });
    DEFAULT_STEPS.forEach((s, i) => addRampStep({ lift: lift.id, position: i, ...s }));
  }
  return true;
}

// The default this app first shipped: 40 × 5, 55 × 3, 70 × 2, 80 × 1, 90 × 1,
// all of the top set, from 8 reps on the bar (5 for a deadlift). A lift still on
// exactly that is one nobody made their own, so it moves to the new default. A
// lift that differs by one rep has been customised and is left alone.
const OLD_STEPS = [[40, 5], [55, 3], [70, 2], [80, 1], [90, 1]];
const onOldDefault = (lift, steps) =>
  steps.length === OLD_STEPS.length
  && steps.every((s, i) => Number(s.percent) === OLD_STEPS[i][0] && Number(s.reps) === OLD_STEPS[i][1]
    && (s.of ?? 'top') === 'top' && rangeTo(s.reps, s.repsTo) == null)
  && Number(lift.startReps) === (lift.kind === 'deadlift' ? 5 : 8)
  && rangeTo(lift.startReps, lift.startRepsTo) == null;

// A squat or bench still starting on bar-and-collars — the old "empty bar",
// 25 — starts on the bar alone. A start the lifter moved anywhere else is theirs,
// and a deadlift's plate-height start has plates on, so collars on.
const kitOf = (lift) => kits.value.find((k) => String(k.id) === String(lift.kit))
  ?? kits.value[kits.value.length - 1] ?? null;
function bareStart(lift) {
  const kit = kitOf(lift);
  if (!kit || lift.kind === 'deadlift') return lift.startWeight;
  const collared = Number(kit.barWeight) + 2 * (Number(kit.collarWeight) || 0);
  return Number(lift.startWeight) === collared ? Number(kit.barWeight) : lift.startWeight;
}

/** Move lifts on an earlier default to the current one. Safe to run on every launch. */
export async function adoptNewDefaults() {
  await Promise.all([kitsReady, liftsReady, rampStepsReady]);
  let moved = 0;
  for (const lift of lifts.value.slice()) {
    const was = Number(lift.schemeVersion) || 1;
    if (Number(lift.id) <= 0 || was >= SCHEME_VERSION) continue;
    const mark = { schemeVersion: SCHEME_VERSION };
    const startWeight = bareStart(lift);
    if (was < 2 && onOldDefault(lift, stepsFor(lift))) {
      saveScheme(lift, { startWeight, ...startRepsFor(lift.kind), steps: DEFAULT_STEPS }, mark);
      moved += 1;
    } else {
      saveLift({ ...lift, startWeight, ...mark });
    }
  }
  return moved;
}

/** The kit everything loads against. v1 has one; the ref exists so a second needs no migration. */
export const currentKit = () => kits.value[kits.value.length - 1] ?? kits.value[0] ?? null;

/** Plates belonging to a kit, heaviest first, only those the gym actually has. */
export const platesFor = (kit) =>
  plates.value
    .filter((p) => !kit || !p.kit || String(p.kit) === String(kit.id))
    .filter((p) => Number(p.pairs) > 0)
    .slice()
    .sort((a, b) => b.weight - a.weight);

/** Steps for a lift, in the order they are lifted. */
export const stepsFor = (lift) =>
  rampSteps.value
    .filter((s) => String(s.lift) === String(lift?.id))
    .slice()
    .sort((a, b) => a.position - b.position);

/** A lift's saved warm-ups as one plain scheme: what the editor edits and today's copy starts from. */
export const savedScheme = (lift) => tidyScheme({ ...lift, steps: stepsFor(lift) });

/**
 * Write a scheme back as the lift's saved one. Step rows are reused in order,
 * so an edit keeps their ids; `patch` goes onto the lift record with the start.
 */
export function saveScheme(lift, scheme, patch = {}) {
  const next = tidyScheme(scheme);
  const rows = stepsFor(lift);
  next.steps.forEach((s, position) => {
    const row = rows[position];
    const fields = { position, ...s };
    if (!row) addRampStep({ lift: lift.id, ...fields });
    else if (Object.keys(fields).some((k) => row[k] !== fields[k])) saveRampStep({ ...row, ...fields });
  });
  for (const row of rows.slice(next.steps.length)) removeRampStep(row.id);
  const { startWeight, startReps, startRepsTo } = next;
  saveLift({ ...(findLift(lift.id) ?? lift), startWeight, startReps, startRepsTo, ...patch });
}
